import { useEffect, useState } from "react";
import {
  strmToTermPrefix,
  type SisBundleSubmissionMode,
} from "@canvas-sync/shared";
import { api } from "../api/client";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { formatDateTime } from "../lib/format";

interface Settings {
  concurrency: number;
  maxRetries: number;
  retryDelayMs: number;
  recentEnrollmentHours: number;
  debug: boolean;
  defaultSubmitToCanvas?: boolean;
  sisBundleDryRun?: boolean;
  allowProdCanvasPost?: boolean;
  sisBundleSubmissionMode?: SisBundleSubmissionMode;
  expectedPrimaryTerm?: string;
  expectedSecondaryTerm?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** The two SIS-bundle posting toggles, keyed by their settings field. */
type SisToggleField = "sisBundleDryRun" | "allowProdCanvasPost";

export function SettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  // Which card the last save belongs to, so the success/error banner renders
  // next to the control the operator actually changed (not in a card that may
  // be scrolled out of view).
  const [saveScope, setSaveScope] = useState<
    "autoSubmit" | "sisBundle" | "terms"
  >("autoSubmit");
  // Draft values for the expected-term inputs — committed on Save, so a
  // half-typed term never round-trips to the API.
  const [termDraft, setTermDraft] = useState<{
    primary: string;
    secondary: string;
  } | null>(null);
  // Set when a DANGEROUS toggle transition needs an explicit type-to-confirm.
  const [confirm, setConfirm] = useState<{
    field: SisToggleField;
    next: boolean;
  } | null>(null);
  // Set when switching the submission mode to "ghost fix only" — that stops
  // SIS data updates flowing to Canvas, so it gets its own type-to-confirm.
  const [confirmMode, setConfirmMode] =
    useState<SisBundleSubmissionMode | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then((s) => {
        if (!cancelled) {
          setSettings(s);
          setTermDraft({
            primary: s.expectedPrimaryTerm ?? "",
            secondary: s.expectedSecondaryTerm ?? "",
          });
        }
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function toggleAutoSubmit(next: boolean) {
    if (!settings) return;
    setSaveScope("autoSubmit");
    setSaveErr(null);
    setSaveMsg(null);
    setSaving(true);
    // Optimistic — flip the toggle before the round-trip so the UI feels
    // snappy. Roll back on failure.
    const prev = settings.defaultSubmitToCanvas;
    setSettings({ ...settings, defaultSubmitToCanvas: next });
    try {
      await api.putSettings({ defaultSubmitToCanvas: next });
      setSaveMsg(
        next
          ? "Auto-submit is ON for syncs you start here and reruns — they'll send their fix CSV to Canvas when they finish. Scheduled syncs are unaffected (they post through the SIS bundle)."
          : "Auto-submit is OFF. Send fix CSVs to Canvas manually from each sync's page.",
      );
    } catch (e) {
      setSettings({ ...settings, defaultSubmitToCanvas: prev });
      setSaveErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // Turning dry-run OFF makes the bundler POST for real; allowing prod POSTs
  // permits irreversible writes to production Canvas. Both are gated by a
  // type-to-confirm dialog. The safe directions apply immediately.
  function isDangerous(field: SisToggleField, next: boolean): boolean {
    return (
      (field === "sisBundleDryRun" && next === false) ||
      (field === "allowProdCanvasPost" && next === true)
    );
  }

  function requestSisToggle(field: SisToggleField, next: boolean) {
    if (isDangerous(field, next)) setConfirm({ field, next });
    else void applySisToggle(field, next);
  }

  async function applySisToggle(field: SisToggleField, next: boolean) {
    if (!settings) return;
    setSaveScope("sisBundle");
    setSaveErr(null);
    setSaveMsg(null);
    setSaving(true);
    const prev = settings[field];
    setSettings({ ...settings, [field]: next });
    try {
      await api.putSettings({ [field]: next });
      setSaveMsg(sisToggleMessage(field, next));
    } catch (e) {
      setSettings({ ...settings, [field]: prev });
      setSaveErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // Switching TO "ghost fix only" silently stops course/user/enrollment
  // updates flowing to Canvas — type-to-confirm guarded. Switching back to
  // the full bundle restores the long-standing default and applies
  // immediately.
  function requestModeChange(next: SisBundleSubmissionMode) {
    if (!settings) return;
    const current = settings.sisBundleSubmissionMode ?? "FULL_BUNDLE";
    if (next === current) return;
    if (next === "GHOST_FIX_ONLY") setConfirmMode(next);
    else void applyMode(next);
  }

  async function applyMode(next: SisBundleSubmissionMode) {
    if (!settings) return;
    setSaveScope("sisBundle");
    setSaveErr(null);
    setSaveMsg(null);
    setSaving(true);
    const prev = settings.sisBundleSubmissionMode;
    setSettings({ ...settings, sisBundleSubmissionMode: next });
    try {
      await api.putSettings({ sisBundleSubmissionMode: next });
      setSaveMsg(
        next === "GHOST_FIX_ONLY"
          ? "Ghost fix only is ON. From the next hourly bundle on, only the ghost-fix CSV is sent to Canvas — the 5 PeopleSoft SIS files are checked and archived but NOT sent."
          : "Full SIS bundle is ON. From the next hourly bundle on, Canvas receives the complete PeopleSoft SIS drop (plus the ghost fix when it's safe to include).",
      );
    } catch (e) {
      setSettings({ ...settings, sisBundleSubmissionMode: prev });
      setSaveErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function saveExpectedTerms() {
    if (!settings || !termDraft) return;
    const primary = termDraft.primary.trim();
    const secondary = termDraft.secondary.trim();
    setSaveScope("terms");
    setSaveErr(null);
    setSaveMsg(null);
    for (const [label, value] of [
      ["Primary", primary],
      ["Secondary", secondary],
    ] as const) {
      if (value !== "" && !/^\d{4}$/.test(value)) {
        setSaveErr(`${label} term must be 4 digits (e.g. 2265).`);
        return;
      }
    }
    setSaving(true);
    try {
      // "" clears the stored value (turns validation off for that feed).
      await api.putSettings({
        expectedPrimaryTerm: primary,
        expectedSecondaryTerm: secondary,
      });
      setSettings({
        ...settings,
        expectedPrimaryTerm: primary || undefined,
        expectedSecondaryTerm: secondary || undefined,
      });
      setTermDraft({ primary, secondary });
      setSaveMsg(
        "Expected terms saved. The Syncs page will highlight any sync whose enrollment file carries a different term.",
      );
    } catch (e) {
      setSaveErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <div className="page-head">
        <h1>Settings</h1>
        <div className="muted">
          Sync defaults and SIS bundle posting controls.
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Auto-submit to Canvas (default)</h2>
        </div>
        {error && <div className="error">{error}</div>}
        {!settings && !error && <div className="muted">Loading…</div>}
        {settings && (
          <>
            <label className="toggle">
              <input
                type="checkbox"
                disabled={saving}
                checked={settings.defaultSubmitToCanvas !== false}
                onChange={(e) => toggleAutoSubmit(e.target.checked)}
              />
              <span className="toggle-track" />
              <span className="toggle-label">
                Automatically send the fix CSV to Canvas after a sync you start
                from the dashboard (and reruns)
              </span>
            </label>
            <div className="muted small" style={{ marginTop: 8 }}>
              <strong>Applies to dashboard-started syncs and reruns.</strong>{" "}
              When on, those syncs send their fix CSV to Canvas as a separate
              upload once they finish; when off, you send it manually from each
              sync's page.
            </div>
            <div className="muted small" style={{ marginTop: 6 }}>
              <strong>Scheduled syncs are not affected.</strong> The hourly SIS
              bundle is the automated path to Canvas — it ships each run's ghost
              fix inside the SIS bundle (full drop or ghost-fix-only, per the
              setting below, with its contradiction safety checks), so scheduled
              runs never send a separate fix upload. This toggle only sets the
              default for the "Start a new sync" page (where it can be
              overridden per sync) and for reruns.
            </div>
            {settings.updatedAt && (
              <div className="muted small" style={{ marginTop: 6 }}>
                Last changed {formatDateTime(settings.updatedAt)}
                {settings.updatedBy ? ` by ${settings.updatedBy}` : ""}.
              </div>
            )}
            {saveScope === "autoSubmit" && saveMsg && (
              <div className="muted small" style={{ marginTop: 8 }}>
                {saveMsg}
              </div>
            )}
            {saveScope === "autoSubmit" && saveErr && (
              <div className="error inline" style={{ marginTop: 8 }}>
                {saveErr}
              </div>
            )}
          </>
        )}
      </div>

      {settings && (
        <div className="card">
          <div className="card-head">
            <h2>SIS bundle posting</h2>
          </div>
          <div className="muted small" style={{ marginBottom: 12 }}>
            Controls what the hourly SIS bundle pipeline sends to Canvas, and
            whether it actually sends it. These take effect on the next bundle
            (each bundle captures the policy when it starts).
          </div>

          {/* Submission mode */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              What gets sent to Canvas
            </div>
            <label
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                cursor: "pointer",
              }}
            >
              <input
                type="radio"
                name="sisBundleSubmissionMode"
                disabled={saving}
                checked={
                  (settings.sisBundleSubmissionMode ?? "FULL_BUNDLE") ===
                  "FULL_BUNDLE"
                }
                onChange={() => requestModeChange("FULL_BUNDLE")}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>Full SIS bundle</strong> (default)
                <div className="muted small">
                  Sends all 5 PeopleSoft files (accounts, courses, enrollments,
                  sections, users) <em>plus</em> the ghost-fix CSV when one is
                  safe to include. This is what keeps Canvas in sync with
                  PeopleSoft.
                </div>
              </span>
            </label>
            <label
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                cursor: "pointer",
                marginTop: 10,
              }}
            >
              <input
                type="radio"
                name="sisBundleSubmissionMode"
                disabled={saving}
                checked={settings.sisBundleSubmissionMode === "GHOST_FIX_ONLY"}
                onChange={() => requestModeChange("GHOST_FIX_ONLY")}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>Ghost fix only</strong>
                <div className="muted small">
                  Sends <em>only</em> the ghost-fix CSV — the file that removes
                  Canvas enrollments that are no longer in PeopleSoft. The 5
                  PeopleSoft files are still checked and archived, but{" "}
                  <strong>not sent</strong>, so Canvas stops receiving
                  course/user/enrollment updates from this pipeline while this
                  is selected. When a bundle has no safe fix to send (no ghosts
                  found, or the fix failed its safety checks), nothing is sent
                  at all. Switching to this requires confirmation.
                </div>
              </span>
            </label>
          </div>

          {/* Dry-run */}
          <label className="toggle">
            <input
              type="checkbox"
              disabled={saving}
              checked={settings.sisBundleDryRun !== false}
              onChange={(e) =>
                requestSisToggle("sisBundleDryRun", e.target.checked)
              }
            />
            <span className="toggle-track" />
            <span className="toggle-label">
              Dry-run: build and archive the SIS bundle zip, but don't POST it
              to Canvas
            </span>
          </label>
          <div className="muted small" style={{ marginTop: 8 }}>
            On (default), the bundler builds the exact Canvas zip and archives
            it, ending at <code>DRY_RUN_BUILT</code> — nothing is sent. Turn it
            off to send SIS bundles to Canvas for real. Turning it off requires
            confirmation.
          </div>

          {/* Allow prod */}
          <label className="toggle" style={{ marginTop: 18 }}>
            <input
              type="checkbox"
              disabled={saving}
              checked={settings.allowProdCanvasPost === true}
              onChange={(e) =>
                requestSisToggle("allowProdCanvasPost", e.target.checked)
              }
            />
            <span className="toggle-track" />
            <span className="toggle-label">
              Allow posting SIS bundles to <strong>production</strong> Canvas
            </span>
          </label>
          <div className="muted small" style={{ marginTop: 8 }}>
            Off (default) fails closed — the bundler refuses to ship a
            production bundle. Enabling this permits{" "}
            <strong>irreversible writes to live student enrollments</strong> and
            requires confirmation. (Even with this on, a bundle still won't POST
            while dry-run is on.)
          </div>

          {saveScope === "sisBundle" && saveMsg && (
            <div className="muted small" style={{ marginTop: 12 }}>
              {saveMsg}
            </div>
          )}
          {saveScope === "sisBundle" && saveErr && (
            <div className="error inline" style={{ marginTop: 12 }}>
              {saveErr}
            </div>
          )}

          {settings.updatedAt && (
            <div className="muted small" style={{ marginTop: 12 }}>
              Last changed {formatDateTime(settings.updatedAt)}
              {settings.updatedBy ? ` by ${settings.updatedBy}` : ""}.
            </div>
          )}
        </div>
      )}

      {settings && termDraft && (
        <div className="card">
          <div className="card-head">
            <h2>Expected enrollment terms</h2>
          </div>
          <div className="muted small" style={{ marginBottom: 12 }}>
            The 4-digit PeopleSoft term each feed is expected to carry — the
            last digit is the semester (3 = SP, 5 = SU, 7 = FA), the middle two
            are the year, so 2267 = FA26. The Syncs page shows every sync's
            detected term and highlights it in amber when it doesn't match the
            expectation for its schedule slot.
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <label>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Primary term
              </div>
              <input
                className="mono"
                type="text"
                inputMode="numeric"
                maxLength={4}
                placeholder="e.g. 2265"
                disabled={saving}
                value={termDraft.primary}
                onChange={(e) =>
                  setTermDraft({ ...termDraft, primary: e.target.value })
                }
                style={{ width: 110 }}
              />
              <div className="muted small" style={{ marginTop: 4 }}>
                Hourly feed (every run except 2 AM).
                {strmToTermPrefix(termDraft.primary.trim()) && (
                  <>
                    {" "}
                    ={" "}
                    <span className="mono">
                      {strmToTermPrefix(termDraft.primary.trim())}
                    </span>
                  </>
                )}
              </div>
            </label>
            <label>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Secondary term
              </div>
              <input
                className="mono"
                type="text"
                inputMode="numeric"
                maxLength={4}
                placeholder="e.g. 2267"
                disabled={saving}
                value={termDraft.secondary}
                onChange={(e) =>
                  setTermDraft({ ...termDraft, secondary: e.target.value })
                }
                style={{ width: 110 }}
              />
              <div className="muted small" style={{ marginTop: 4 }}>
                2 AM (Pacific) feed.
                {strmToTermPrefix(termDraft.secondary.trim()) && (
                  <>
                    {" "}
                    ={" "}
                    <span className="mono">
                      {strmToTermPrefix(termDraft.secondary.trim())}
                    </span>
                  </>
                )}
              </div>
            </label>
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn"
              disabled={saving}
              onClick={() => void saveExpectedTerms()}
            >
              {saving && saveScope === "terms" ? "Saving…" : "Save terms"}
            </button>
          </div>
          <div className="muted small" style={{ marginTop: 10 }}>
            <strong>Display only.</strong> These values are used solely for
            validation on this dashboard — they do not change which term
            PeopleSoft extracts or what is sent to Canvas.{" "}
            <strong>
              A ticket should be entered for any actual term changes.
            </strong>{" "}
            Leave a field blank to turn its check off.
          </div>
          {saveScope === "terms" && saveMsg && (
            <div className="muted small" style={{ marginTop: 8 }}>
              {saveMsg}
            </div>
          )}
          {saveScope === "terms" && saveErr && (
            <div className="error inline" style={{ marginTop: 8 }}>
              {saveErr}
            </div>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2>Advanced settings</h2>
        </div>
        <div className="muted small" style={{ marginBottom: 10 }}>
          These tune how the worker processes each sync. Only change them if you
          know what you're doing.
        </div>
        {settings && (
          <dl className="settings-grid">
            <dt>Concurrency</dt>
            <dd className="mono">{settings.concurrency}</dd>
            <dt>Max retries</dt>
            <dd className="mono">{settings.maxRetries}</dd>
            <dt>Retry delay (ms)</dt>
            <dd className="mono">{settings.retryDelayMs}</dd>
            <dt>Recent enrollment grace (h)</dt>
            <dd className="mono">{settings.recentEnrollmentHours}</dd>
            <dt>Debug</dt>
            <dd className="mono">{String(settings.debug)}</dd>
          </dl>
        )}
        <details className="raw-details">
          <summary>Show technical details</summary>
          <div className="raw-block" style={{ whiteSpace: "normal" }}>
            Worker tuning values are currently served from a stub — persistence
            ships in pass 2.
          </div>
        </details>
      </div>

      {confirmMode && (
        <ConfirmDialog
          title="Send only the ghost fix to Canvas"
          message={
            "From the next hourly bundle on, ONLY the ghost-fix CSV will be sent to Canvas. " +
            "The 5 PeopleSoft SIS files (accounts, courses, enrollments, sections, users) will NOT be sent, " +
            "so Canvas stops receiving roster updates from this pipeline until you switch back to the full bundle. " +
            "Bundles that have no safe fix will send nothing at all."
          }
          confirmPhrase="ghost fix only"
          confirmLabel="Switch to ghost fix only"
          cancelLabel="Cancel"
          danger
          busy={saving}
          onConfirm={async () => {
            const next = confirmMode;
            await applyMode(next);
            setConfirmMode(null);
          }}
          onCancel={() => setConfirmMode(null)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={
            confirm.field === "allowProdCanvasPost"
              ? "Allow PRODUCTION Canvas posting"
              : "Turn off dry-run"
          }
          message={
            confirm.field === "allowProdCanvasPost"
              ? "This permits the bundler to POST SIS bundles to PRODUCTION Canvas — irreversible changes to live student enrollments. Only do this when you intend to ship to prod."
              : "This makes the bundler POST SIS bundles to Canvas for real, instead of only building and archiving the zip. Make sure you've validated the archived dry-run zips first."
          }
          confirmPhrase={
            confirm.field === "allowProdCanvasPost"
              ? "post to production"
              : "go live"
          }
          confirmLabel={
            confirm.field === "allowProdCanvasPost"
              ? "Allow prod posting"
              : "Turn off dry-run"
          }
          cancelLabel="Cancel"
          danger
          busy={saving}
          onConfirm={async () => {
            const c = confirm;
            await applySisToggle(c.field, c.next);
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </section>
  );
}

function sisToggleMessage(field: SisToggleField, next: boolean): string {
  if (field === "sisBundleDryRun") {
    return next
      ? "Dry-run is ON. SIS bundles are built and archived but not sent to Canvas."
      : "Dry-run is OFF. SIS bundles will be POSTed to Canvas for real on the next drop.";
  }
  return next
    ? "Production Canvas posting is ALLOWED. SIS bundles targeting prod can now be sent (subject to dry-run)."
    : "Production Canvas posting is BLOCKED. The bundler will refuse to ship production bundles.";
}
